<template>
  <button
    :class="combinedClass"
    :aria-label="ariaLabel || undefined"
    :title="title || undefined"
    :type="type || undefined"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <img
      v-if="isLoading"
      class="loading h-6 w-6"
      src="@/assets/loading-grey.svg"
      alt="loading"
    />

    <slot v-else />
  </button>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'default',
    validator: (v) =>
      ['default', 'primary', 'secondary', 'subtle', 'transparent'].includes(v),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'button',
    validator: (v) => ['button', 'submit'].includes(v),
  },
  title: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  height: {
    type: String,
    default: 'h-12 md:h-9',
  },
  padding: {
    type: String,
    default: 'px-4 py-2',
  },
  borderRadius: {
    type: String,
    default: '!rounded-md',
  },
});

defineEmits(['click']);

const baseClass = computed(() => {
  return `${props.padding} ${props.height} ${props.borderRadius} flex items-center min-w-max justify-center text-sm font-medium transition-colors duration-100 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-400`;
});

const variantClasses = {
  default:
    'bg-steel-100 text-steel-900 hover:bg-steel-300 active:bg-steel-400 disabled:opacity-50 disabled:hover:bg-steel-100 disabled:active:bg-steel-100',
  primary:
    'bg-sun-400 text-steel-1100 hover:bg-sun-500 active:bg-sun-600 disabled:bg-steel-300 disabled:opacity-50',
  secondary:
    'bg-steel-900 text-steel-200 hover:bg-steel-1000 active:bg-steel-1100 disabled:bg-steel-900 disabled:text-steel-400',
  subtle:
    'border border-steel-300 bg-white text-steel-900 hover:bg-steel-300 active:bg-steel-400 disabled:opacity-50 disabled:hover:bg-transparent disabled:active:bg-transparent',
  transparent:
    'border border-steel-300 text-steel-900 hover:bg-steel-300 active:bg-steel-400 disabled:opacity-50 disabled:hover:bg-transparent disabled:active:bg-transparent',
  'transparent-no-border':
    'text-steel-900 hover:bg-steel-300 active:bg-steel-400 disabled:opacity-50 disabled:hover:bg-transparent disabled:active:bg-transparent',
};

const combinedClass = computed(
  () => `${baseClass.value} ${variantClasses[props.variant]}`
);
</script>
